import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from "../config";

const Marketplace = () => {
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const elementCategories = [
    { value: 'all', label: 'All', icon: '✨' },
    { value: 'earth', label: 'Earth', icon: '🏔️' },
    { value: 'water', label: 'Water', icon: '💧' },
    { value: 'fire', label: 'Fire', icon: '🔥' },
    { value: 'air', label: 'Air', icon: '🌬️' },
    { value: 'space', label: 'Space', icon: '✨' }
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/marketplace/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper to dynamically match product name/category to its Vastu Element tag
  const getElementTag = (product) => {
    const name = product.name.toLowerCase();
    const cat = product.category.toLowerCase();
    
    if (name.includes('tortoise') || name.includes('brass')) {
      return { label: 'Fire Element', color: 'text-[#C62828]' };
    }
    if (name.includes('bamboo') || name.includes('plant') || cat === 'plants') {
      return { label: 'Wood Element', color: 'text-[#2E7D32]' };
    }
    if (name.includes('salt') || name.includes('crystal') || cat === 'crystals') {
      return { label: 'Earth Element', color: 'text-[#826239]' };
    }
    if (name.includes('pyramid') || cat === 'pyramids') {
      return { label: 'Space Element', color: 'text-[#6A1B9A]' };
    }
    if (name.includes('fountain') || name.includes('flow')) {
      return { label: 'Water Element', color: 'text-[#1565C0]' };
    }
    if (name.includes('chime') || name.includes('wind') || cat === 'remedies' && name.includes('rods')) {
      return { label: 'Air Element', color: 'text-[#00838F]' };
    }
    return { label: 'Earth Element', color: 'text-[#826239]' };
  };

  // Category Filtering
  const filteredProducts = products.filter(product => {
    if (selectedCategory === 'all') return true;
    const tag = getElementTag(product).label.toLowerCase();
    return tag.includes(selectedCategory);
  });

  // Search Filtering
  const searchedProducts = filteredProducts.filter(product => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return product.name.toLowerCase().includes(q) || product.description.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen pt-28 pb-20 bg-[#F9F6F0] relative overflow-hidden">
      {/* Background drifting blobs */}
      <div className="bg-blob blob-gold top-[10%] left-[-5%] w-[450px] h-[450px] opacity-40" />
      <div className="bg-blob blob-saffron bottom-[10%] right-[-5%] w-[500px] h-[500px] opacity-40" />

      <div className="container mx-auto max-w-6xl px-6 md:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-[10px] font-bold tracking-widest uppercase text-[#D97706] mb-2 block">
            Ancient Wisdom, Modern Living
          </span>
          <h1 className="text-3xl md:text-4xl font-bold font-vedic text-[#352520] mb-4">
            Remedial Marketplace
          </h1>
          <p className="text-sm text-[#5D4E37] max-w-2xl mx-auto font-light leading-relaxed">
            Curated artifacts designed to harmonize your living space. Each piece is vetted by our AI for energetic alignment and aesthetic grace.
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-10 relative">
          <input
            type="text"
            placeholder="Search for remedies (e.g. 'Abundance', 'Peace')"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border border-[#EBE6DD] rounded-full pl-14 pr-6 py-3.5 text-sm font-medium text-[#352520] placeholder-[#826239]/60 shadow-sm focus:outline-none focus:border-[#D97706] focus:ring-1 focus:ring-[#D97706]/20 transition-all"
          />
          <svg className="w-5 h-5 text-[#826239]/50 absolute left-6 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Category Filter Pills */}
        <div className="flex flex-wrap gap-4 justify-center mb-12">
          {elementCategories.map((category) => (
            <button
              key={category.value}
              onClick={() => setSelectedCategory(category.value)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold tracking-wide transition-all shadow-sm ${
                selectedCategory === category.value
                  ? 'bg-[#826239] text-white border border-[#826239]'
                  : 'bg-white text-[#826239] border border-[#EBE6DD] hover:border-[#D97706]'
              }`}
            >
              <span>{category.icon}</span>
              <span>{category.label}</span>
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="w-12 h-12 border-4 border-[#826239] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm text-[#826239] font-medium">Loading products...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {searchedProducts.map((product) => {
              const elementInfo = getElementTag(product);
              return (
                <div
                  key={product.id}
                  className="bg-white border border-[#EBE6DD]/60 rounded-3xl p-5 shadow-lg flex flex-col justify-between hover:shadow-xl transition-all duration-300"
                >
                  <div>
                    {/* Image and Element Badge */}
                    <div className="relative aspect-square rounded-2xl overflow-hidden mb-5 bg-[#FAF6EE] border border-[#EBE6DD]/30">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                      <span className="absolute top-3.5 right-3.5 bg-white/95 backdrop-blur-md px-3.5 py-1 rounded-full text-[10px] font-bold text-[#826239] border border-[#EBE6DD]/50 uppercase tracking-wider shadow-sm">
                        {elementInfo.label}
                      </span>
                    </div>

                    {/* Info */}
                    <div className="flex justify-between items-start gap-4 mb-3">
                      <h3 className="text-base font-bold font-vedic text-[#352520] leading-tight">
                        {product.name}
                      </h3>
                      <span className="text-sm font-bold text-[#D97706] whitespace-nowrap">
                        ₹{product.price.toLocaleString('en-IN')}
                      </span>
                    </div>

                    <p className="text-xs text-[#5D4E37] leading-relaxed font-light mb-6 flex-grow text-left">
                      {product.description}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 mt-auto">
                    <button className="flex-grow bg-[#D97706] hover:bg-[#C25E03] text-white py-3.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 shadow-md active:scale-97">
                      Add to Cart
                    </button>
                    <button className="w-12 h-12 border border-[#EBE6DD] hover:border-[#D97706] rounded-full flex items-center justify-center text-[#826239] hover:text-[#D97706] transition-colors bg-white shadow-sm active:scale-97">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && searchedProducts.length === 0 && (
          <div className="text-center py-20 bg-white/40 rounded-3xl border border-[#EBE6DD]/60 p-8">
            <p className="text-[#826239] text-base font-light">No Vastu remedies match your search criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Marketplace;
